import { ConvexClient } from "convex/browser";
import type { FunctionReference } from "convex/server";
import { inject, provide } from "vue";
import type { ConvexAuthTokenFetcher, ConvexVueClientLike } from "./types";

const convexVueClientKey = Symbol("ConvexVueClient");

export interface CreateConvexVueClientOptions {
  url?: string;
  client?: ConvexVueClientLike;
}

export class ConvexVueClient implements ConvexVueClientLike {
  private readonly client: ConvexVueClientLike;

  constructor(options: CreateConvexVueClientOptions) {
    if (!options.client && !options.url) {
      throw new Error("ConvexVueClient requires either a Convex URL or an existing client.");
    }
    this.client = options.client ?? (new ConvexClient(options.url as string) as unknown as ConvexVueClientLike);
  }

  query<Query extends FunctionReference<"query">>(query: Query, args?: Query["_args"]) {
    return this.client.query(query, args);
  }

  mutation<Mutation extends FunctionReference<"mutation">>(mutation: Mutation, args?: Mutation["_args"]) {
    return this.client.mutation(mutation, args);
  }

  action<Action extends FunctionReference<"action">>(action: Action, args?: Action["_args"]) {
    return this.client.action(action, args);
  }

  watchQuery<Query extends FunctionReference<"query">>(query: Query, args?: Query["_args"]) {
    if (!this.client.watchQuery) {
      throw new Error("The active Convex client does not support watchQuery.");
    }
    return this.client.watchQuery(query, args);
  }

  onUpdate<Query extends FunctionReference<"query">>(
    query: Query,
    args: Query["_args"],
    callback: (value: Query["_returnType"]) => void,
    onError?: (error: Error) => void,
  ) {
    if (this.client.onUpdate) {
      return this.client.onUpdate(query, args, callback, onError);
    }
    const watch = this.watchQuery(query, args);
    const emitValue = () => {
      try {
        callback(watch.localQueryResult());
      } catch (error) {
        if (error instanceof Error && onError) {
          onError(error);
          return;
        }
        throw error;
      }
    };
    emitValue();
    return watch.onUpdate(emitValue);
  }

  setAuth(fetchToken: ConvexAuthTokenFetcher, onChange?: (isAuthenticated: boolean) => void) {
    this.client.setAuth(fetchToken, onChange);
  }

  clearAuth() {
    this.client.clearAuth?.();
  }

  getAuth() {
    return this.client.getAuth?.() ?? null;
  }
}

export function createConvexVueClient(options: CreateConvexVueClientOptions): ConvexVueClient {
  return new ConvexVueClient(options);
}

export function provideConvexVueClient(client: ConvexVueClientLike) {
  provide(convexVueClientKey, client);
}

export function useConvexVueClient(): ConvexVueClientLike {
  const client = inject<ConvexVueClientLike | undefined>(convexVueClientKey, undefined);
  if (!client) {
    throw new Error("Could not find Convex Vue client. Did you forget to call provideConvexVueClient?");
  }
  return client;
}
